/**
 * ModularPatientSelfDataContext.js
 *
 * Service-user context for patients on subcollection-based assessment modules
 * (Adult Autism, future CYP modules). The patient logs in, and this context
 * loads their header document + all form docs from the pathway subcollection.
 *
 * Key differences from PatientSelfDataContext (ADHD):
 *
 * 1. READS: Fetches `patients/{uid}` (slim header) +
 *    `patients/{uid}/adult_autism/assessment_001/formInstances/*` rather than relying on
 *    a monolithic document with embedded formData.
 *
 * 2. WRITES: Per-document updateDoc on individual formInstance docs.
 *
 * 3. DEBOUNCING: Each formKey has its own independent debounce timer (via
 *    useModularFormInstances).
 *
 * 4. GUARD: Only activates for service users whose assessmentModule claim is NOT
 *    'adultAdhd'. ADHD patients continue using PatientSelfDataContext.
 */
import React, { createContext, useCallback, useMemo } from 'react';
import { db } from '../firebase';
import { collection, doc, getDoc, getDocs } from 'firebase/firestore';
import { useAuth } from '../hooks/useAuth';
import { useModularFormInstances } from '../hooks/useModularFormInstances';
import { isModularModule, getModuleCollection, getModuleAssessmentDocId } from '../constants/assessmentModules';
import { assessmentTypeFromModule } from '../analytics/serviceUserFormEvents';

export const ModularPatientSelfDataContext = createContext();

const LOCALSTORAGE_KEY = 'td_modular_self_patient_data';

export const ModularPatientSelfDataProvider = ({ children }) => {
  const { user: currentUser, claims } = useAuth();

  const assessmentModule = claims?.assessmentModule;

  const isModularServiceUser =
    claims?.accountType === 'Service User' &&
    claims?.userType === 'Patient' &&
    isModularModule(assessmentModule);

  // Resolve the pathway subcollection segment and assessment doc ID from the
  // registry so the context reads/writes the correct tree for any modular module.
  const moduleCollection = useMemo(
    () => getModuleCollection(assessmentModule),
    [assessmentModule],
  );
  const assessmentDocId = useMemo(
    () => getModuleAssessmentDocId(assessmentModule),
    [assessmentModule],
  );

  const fetchData = useCallback(async () => {
    const patientDocRef = doc(db, 'patients', currentUser.uid);
    const [patientDoc, formInstancesSnap] = await Promise.all([
      getDoc(patientDocRef),
      getDocs(collection(db, 'patients', currentUser.uid, moduleCollection, assessmentDocId, 'formInstances')),
    ]);

    if (!patientDoc.exists()) {
      throw new Error('Patient record not found');
    }

    const headerData = patientDoc.data();
    const formInstancesData = {};
    formInstancesSnap.forEach((docSnap) => {
      formInstancesData[docSnap.id] = docSnap.data();
    });

    return { header: headerData, formInstances: formInstancesData };
  }, [currentUser, moduleCollection, assessmentDocId]);

  const getFormInstanceDocRef = useCallback((formKey) => (
    doc(db, 'patients', currentUser.uid, moduleCollection, assessmentDocId, 'formInstances', formKey)
  ), [currentUser, moduleCollection, assessmentDocId]);

  const {
    header,
    formInstances,
    loading,
    error,
    fetchEntityData,
    updateFormAnswers,
    clearData,
  } = useModularFormInstances({
    enabled: isModularServiceUser && !!currentUser,
    entityId: currentUser?.uid || null,
    localStorageKey: LOCALSTORAGE_KEY,
    fetchData,
    getFormInstanceDocRef,
    // The claim is 'adultAutism' / 'cypAdhd'; the events carry the canonical
    // enum ('autism' / 'cyp_adhd') and baseFields drops any event whose
    // assessment_type is not one of those. Passing the raw claim through
    // silently stopped every service-user form event on this pathway.
    analyticsContext: {
      role: 'patient',
      assessmentType: assessmentTypeFromModule(assessmentModule),
    },
  });

  return (
    <ModularPatientSelfDataContext.Provider
      value={{
        header,
        formInstances,
        loading,
        error,
        fetchPatientData: fetchEntityData,
        updateFormAnswers,
        clearPatientData: clearData,
      }}
    >
      {children}
    </ModularPatientSelfDataContext.Provider>
  );
};
